/**
 * P-007 c3 — per-record-type conflict resolution for offline multi-device merge.
 *
 * When a record edited offline is reconciled against the server copy on reconnect, the merge
 * strategy is keyed by health-domain type (the claim's core distinction):
 *   - medication  → last-write-wins, but the losing version is retained in an audit trail (never
 *                   silently dropped — medication changes are safety-critical)
 *   - vital/glucose/lab → additive merge (readings are time-series points; union by timestamp)
 *   - allergy     → union merge (never lose a reported allergy; the union is the safe superset)
 *   - appointment → user-prompt (cannot auto-resolve; flag for the patient to choose)
 *   - default     → last-write-wins by timestamp
 * Pure/deterministic (unit-testable). Each side is { value, updatedAt, deviceId }.
 */
const TS = (r) => new Date(r?.updatedAt || 0).getTime();

export function resolveConflict(type, local, remote) {
  switch (String(type || '').toLowerCase()) {
    case 'medication':
      return _lwwWithAudit(local, remote);
    case 'vital':
    case 'glucose':
    case 'lab':
      return _additiveMerge(local, remote);
    case 'allergy':
      return _unionMerge(local, remote);
    case 'appointment':
      return { strategy: 'user-prompt', resolved: null, needsUser: true, candidates: [local, remote] };
    default:
      return { strategy: 'lww', resolved: TS(local) >= TS(remote) ? local : remote, needsUser: false };
  }
}

function _lwwWithAudit(local, remote) {
  const winner = TS(local) >= TS(remote) ? local : remote;
  const loser = winner === local ? remote : local;
  return { strategy: 'lww+audit', resolved: winner, needsUser: false, audit: [loser] };
}

function _additiveMerge(local, remote) {
  // Union the reading arrays by a stable key (timestamp+value); order by time.
  const readings = [...(local?.value?.readings || []), ...(remote?.value?.readings || [])];
  const seen = new Map();
  for (const r of readings) seen.set(`${r.t}|${r.v}`, r);
  const merged = [...seen.values()].sort((a, b) => new Date(a.t) - new Date(b.t));
  return { strategy: 'additive', resolved: { ...remote?.value, ...local?.value, readings: merged }, needsUser: false };
}

function _unionMerge(local, remote) {
  const items = new Set([...(local?.value?.items || []), ...(remote?.value?.items || [])]);
  return { strategy: 'union', resolved: { items: [...items] }, needsUser: false };
}
